/** Utilisateurs (section 5) : liste (recherche, filtres établissement/rôle, tri, menu ⋯), fiche
 * pleine page, et modales Changer le mot de passe / Envoyer l'accès / Se connecter en tant que. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';
import { adminApi } from '../../../services/api';
import { Button } from '../../../components/ui/kit';
import { useSortableTable, SortableTh } from '../useSortableTable';
import { useAutoSave } from '../../../hooks/useAutoSave';
import { FullPanel, PanelCard, Field } from '../FullPanel';
import { ROLES, ROLE_LABELS, ROLE_DESCRIPTIONS, TENANT_TYPE_LABELS, ACTION_LABELS } from '../constants';
import { timeAgo, dateTime } from '../format';

function Modal({ title, onClose, children, testId }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-theme="admin">
      <div className="bg-paper border border-hair rounded-lg p-5 w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <h3 className="font-title text-2xl mb-4">{title}</h3>
        {children}
      </div>
    </div>, document.body);
}

function genPassword() {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 14 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function SetPasswordModal({ user, onClose }) {
  const [pw, setPw] = useState(''); const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [requireChange, setRequireChange] = useState(true); const [notify, setNotify] = useState(false);
  const valid = pw.length >= 10 && pw === confirm;
  const submit = async () => {
    try { await adminApi.setPassword(user.id, { password: pw, require_change: requireChange, notify });
      toast.success('Mot de passe défini'); onClose(); }
    catch (e) { toast.error(e.message); }
  };
  const generate = () => { const g = genPassword(); setPw(g); setConfirm(g); setShow(true); navigator.clipboard?.writeText(g); toast.success('Mot de passe généré et copié'); };
  return (
    <Modal title="Changer le mot de passe" onClose={onClose} testId="set-password-modal">
      <Field label="Nouveau mot de passe">
        <div className="flex gap-2">
          <input className="vd-input" type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="pw-new" />
          <Button onClick={() => setShow((s) => !s)}>{show ? 'Masquer' : 'Afficher'}</Button>
        </div>
      </Field>
      <Field label="Confirmer"><input className="vd-input" type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pw-confirm" /></Field>
      <button className="vd-link text-sm mb-3" onClick={generate} data-testid="pw-generate">Générer un mot de passe</button>
      <p className={`text-xs mb-3 ${valid ? 'text-n500' : 'adm-text-error'}`}>10 caractères minimum et les deux saisies identiques.</p>
      <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={requireChange} onChange={(e) => setRequireChange(e.target.checked)} /> Exiger un nouveau mot de passe à la prochaine connexion</label>
      <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Prévenir l'utilisateur par email (sans le mot de passe)</label>
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="primary" disabled={!valid} onClick={submit} data-testid="pw-submit">Changer</Button>
      </div>
    </Modal>
  );
}

function SendAccessModal({ user, tenant, onClose }) {
  const [includeLink, setIncludeLink] = useState(true);
  const [publicUrl, setPublicUrl] = useState('');
  useEffect(() => { adminApi.settings().then((s) => setPublicUrl(s.instance?.public_url || '')).catch(() => {}); }, []);
  const submit = async () => {
    try { const r = await adminApi.sendAccess(user.id, { include_link: includeLink });
      if (r.email_sent) toast.success("Email d'accès envoyé"); else toast.error("Jeton créé mais l'email n'a pu être envoyé");
      onClose(); } catch (e) { toast.error(e.message); }
  };
  const first = (user.name || user.email).split(' ')[0];
  return (
    <Modal title="Envoyer l'email d'accès" onClose={onClose} testId="send-access-modal">
      <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={includeLink} onChange={(e) => setIncludeLink(e.target.checked)} /> Inclure un lien pour définir le mot de passe (valable 72 h)</label>
      <div className="vd-card p-4 text-sm mb-4 bg-surface/40">
        <p className="text-xs text-n600">De : {tenant?.name ? `${tenant.name} ` : ''}no-reply@cavorama.com</p>
        <p className="text-xs text-n600 mb-2">À : {user.email}</p>
        <p className="font-medium mb-2">Objet : Vos accès Cavorama — Établissement</p>
        <p>Bonjour {first},</p>
        <p className="mt-1">Adresse : <span className="adm-mono text-xs">{publicUrl || '— (URL publique à définir dans Paramètres)'}</span></p>
        <p>Identifiant : <span className="adm-mono text-xs">{user.email}</span></p>
        {includeLink && <p className="mt-2"><span className="vd-btn vd-btn-primary" style={{ pointerEvents: 'none' }}>Définir mon mot de passe</span></p>}
      </div>
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="primary" onClick={submit} data-testid="send-access-submit">Envoyer</Button>
      </div>
    </Modal>
  );
}

function ImpersonateModal({ user, tenant, onClose }) {
  const [mode, setMode] = useState('direct');
  const blocked = !user.active || (tenant && !tenant.active);
  const open = async () => {
    try {
      if (mode === 'direct') {
        const r = await adminApi.impersonate(user.id, { mode: 'direct' });
        if (r.warning) (toast.warning || toast)(r.warning);
        await navigator.clipboard?.writeText(`${window.location.origin}/impersonate?token=${encodeURIComponent(r.token)}`);
        toast.success('Lien de connexion directe copié (valable 4 min)'); onClose();
      } else {
        window.open(`/login?email=${encodeURIComponent(user.email)}`, '_blank');
        toast.success('Page de connexion ouverte dans un nouvel onglet'); onClose();
      }
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Modal title="Se connecter avec cet utilisateur" onClose={onClose} testId="impersonate-modal">
      {blocked && <p className="adm-badge-warn px-2 py-1 rounded text-sm mb-3 inline-block">Attention : le compte ou son établissement est bloqué.</p>}
      <label className="flex items-start gap-2 text-sm mb-2"><input type="radio" checked={mode === 'direct'} onChange={() => setMode('direct')} className="mt-1" />
        <span><b>Connexion directe</b> — lien ouvrant une session avec les droits de {user.name}, sans mot de passe (jeton à usage unique valable 4 min, session de 30 min). À ouvrir dans une fenêtre normale ou privée.</span></label>
      <label className="flex items-start gap-2 text-sm mb-4"><input type="radio" checked={mode === 'prefill'} onChange={() => setMode('prefill')} className="mt-1" />
        <span><b>Page de connexion pré-remplie</b> — seul l'email est pré-saisi. Le mot de passe ne peut pas l'être (il est stocké haché).</span></label>
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="primary" onClick={open} data-testid="impersonate-open">{mode === 'direct' ? 'Copier le lien' : 'Ouvrir dans un nouvel onglet'}</Button>
      </div>
    </Modal>
  );
}

function RowMenu({ user, tenant, onAction }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 220) });
    }
    setOpen((o) => !o);
  };
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close); window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [open]);
  const Item = ({ children, action, danger }) => (
    <button className={`block w-full text-left px-3 py-2 text-sm hover:bg-surface ${danger ? 'text-red-700' : ''}`}
      onClick={(e) => { e.stopPropagation(); setOpen(false); onAction(action); }} data-testid={`user-menu-${action}`}>{children}</button>
  );
  return (
    <>
      <button ref={btnRef} className="vd-link px-2" onClick={(e) => { e.stopPropagation(); toggle(); }} data-testid={`user-menu-btn-${user.email}`}>⋯</button>
      {open && createPortal(
        <div className="fixed z-[80] w-[220px] bg-paper border border-hair rounded-md shadow-md py-1" style={{ top: pos.top, left: pos.left }} data-theme="admin" onClick={(e) => e.stopPropagation()}>
          <Item action="edit">Modifier</Item>
          <Item action="reset">Réinitialiser le mot de passe</Item>
          <Item action="setpw">Changer le mot de passe…</Item>
          <Item action="access">Envoyer l'email d'accès…</Item>
          <Item action="impersonate">Se connecter avec cet utilisateur…</Item>
          <div className="hair-t my-1" />
          <Item action="delete" danger>Supprimer</Item>
        </div>, document.body)}
    </>
  );
}

function UserPanel({ user, tenants, onBack, onSaved, reload, openModal }) {
  const isNew = !user.id;
  const [form, setForm] = useState({
    first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '',
    phone: user.phone || '', role: user.role || 'gerant', tenant_id: user.tenant?.id || user.tenant_id || tenants[0]?.id,
    send_access: true, password: '',
  });
  const [dirty, setDirty] = useState(false);
  const [events, setEvents] = useState([]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  useEffect(() => { if (!isNew) adminApi.logs({ user: user.email, limit: 6 }).then((r) => setEvents(r.rows || [])); }, [isNew, user.email]);

  // Enregistrement AUTOMATIQUE (fiche existante).
  const autoPayload = { first_name: form.first_name, last_name: form.last_name, email: form.email,
    phone: form.phone, role: form.role, tenant_id: form.tenant_id };
  const autoStatus = useAutoSave(isNew ? {} : autoPayload, async (v) => {
    if (isNew) return;
    await adminApi.updateUser(user.id, v); onSaved();
  });

  const save = async () => {
    try {
      await adminApi.createUser({ ...form }); toast.success('Compte créé');
      setDirty(false); onSaved(); onBack();
    } catch (e) { toast.error(e.message || 'Création impossible'); }
  };
  const back = () => { if (isNew && dirty && !window.confirm('Des modifications ne sont pas enregistrées. Quitter ?')) return; onBack(); };
  const del = async () => { if (!window.confirm(`Supprimer ${user.email} ?`)) return;
    try { await adminApi.deleteUser(user.id); toast.success('Compte supprimé'); onBack(); onSaved(); } catch (e) { toast.error(e.message); } };
  const toggle = async () => { try { if (user.active) await adminApi.blockUser(user.id); else await adminApi.unblockUser(user.id);
    toast.success(user.active ? 'Compte bloqué' : 'Compte débloqué'); reload(); } catch (e) { toast.error(e.message); } };

  return (
    <FullPanel testId="user-panel" title={isNew ? 'Nouvel utilisateur' : user.name}
      subtitle={isNew ? null : user.email} onBack={back} dirty={dirty} onCancel={back}
      onSave={isNew ? save : undefined} autoSaveStatus={isNew ? undefined : autoStatus}
      saveLabel="Créer le compte">
      <div className="adm-panel-grid">
        <PanelCard title="Identité" testId="user-identity">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Prénom"><input className="vd-input" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} /></Field>
            <Field label="Nom"><input className="vd-input" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} /></Field>
          </div>
          <Field label="Email (identifiant de connexion)"><input className="vd-input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="user-email" /></Field>
          <Field label="Téléphone"><input className="vd-input" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
        </PanelCard>

        <PanelCard title="Rattachement" testId="user-attach">
          <Field label="Établissement">
            <select className="vd-input" value={form.tenant_id} onChange={(e) => set('tenant_id', e.target.value)} data-testid="user-tenant">
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Rôle">
            <select className="vd-input" value={form.role} onChange={(e) => set('role', e.target.value)} data-testid="user-role">
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </Field>
          <p className="text-xs text-n600">{ROLE_DESCRIPTIONS[form.role]}</p>
          {isNew && (
            <>
              <Field label="Mot de passe initial (optionnel — sinon aléatoire)"><input className="vd-input" value={form.password} onChange={(e) => set('password', e.target.value)} /></Field>
              <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={form.send_access} onChange={(e) => set('send_access', e.target.checked)} /> Envoyer l'email d'accès à la création</label>
            </>
          )}
        </PanelCard>

        {!isNew && (
          <>
            <PanelCard title="Accès" testId="user-access-card">
              <p className="text-sm mb-1">État : <span className={`adm-badge ${user.active ? 'adm-badge-active' : ''}`}>{user.active ? 'actif' : 'bloqué'}</span></p>
              <p className="text-sm mb-3">Dernière connexion : {user.last_login_at ? dateTime(user.last_login_at) : 'jamais'}</p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={toggle}>{user.active ? 'Bloquer' : 'Débloquer'}</Button>
                <Button onClick={() => openModal('reset', user)}>Réinitialiser</Button>
                <Button onClick={() => openModal('setpw', user)}>Changer le MDP…</Button>
                <Button onClick={() => openModal('access', user)}>Envoyer l'accès…</Button>
              </div>
              <div className="flex flex-col gap-4 mt-4 pt-3 hair-t">
                <div>
                  <p className="text-[13px] text-n700 mb-1">Connexion directe</p>
                  <div className="flex items-center gap-2">
                    <span className="adm-mono text-xs text-n500 truncate max-w-[340px]" title="Généré au clic (valable 4 min) — à ouvrir dans une fenêtre normale ou privée">{window.location.origin}/impersonate?token=…</span>
                    <Button data-testid="impersonate-direct" onClick={async () => {
                      try { const r = await adminApi.impersonate(user.id, { mode: 'direct' });
                        if (r.warning) (toast.warning || toast)(r.warning);
                        await navigator.clipboard?.writeText(`${window.location.origin}/impersonate?token=${encodeURIComponent(r.token)}`);
                        toast.success('Lien de connexion directe copié (valable 4 min)'); }
                      catch (e) { toast.error(e.message); }
                    }}>Copier</Button>
                  </div>
                </div>
                <div>
                  <p className="text-[13px] text-n700 mb-1">Page de connexion pré-remplie</p>
                  <div className="flex items-center gap-2">
                    <span className="adm-mono text-xs text-n500 truncate max-w-[340px]">{`${window.location.origin}/login?email=${user.email}`}</span>
                    <Button data-testid="impersonate-prefill" onClick={() => {
                      navigator.clipboard?.writeText(`${window.location.origin}/login?email=${encodeURIComponent(user.email)}`);
                      toast.success('Lien de connexion pré-rempli copié');
                    }}>Copier</Button>
                  </div>
                </div>
              </div>
            </PanelCard>

            <PanelCard title="Activité récente" testId="user-activity">
              <ul className="space-y-1">
                {events.map((e, i) => (
                  <li key={i} className="text-sm flex gap-2"><span className="text-xs text-n500 w-20 shrink-0">{timeAgo(e.date)}</span>{ACTION_LABELS[e.action] || e.action}</li>
                ))}
                {!events.length && <li className="text-sm text-n500">Aucune activité.</li>}
              </ul>
            </PanelCard>

            <PanelCard title="Zone sensible" testId="user-danger" className="border-red-700/40">
              <Button onClick={del} className="text-red-700 border-red-700/50" data-testid="user-delete">Supprimer le compte</Button>
            </PanelCard>
          </>
        )}
      </div>
    </FullPanel>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState(''); const [fTenant, setFTenant] = useState(''); const [fRole, setFRole] = useState('');
  const [open, setOpen] = useState(null);
  const [modal, setModal] = useState(null); // {type, user}
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const undoRef = useRef({});

  const load = () => Promise.all([adminApi.users(), adminApi.tenants()]).then(([u, t]) => { setUsers(u); setTenants(t); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get('tenant')) setFTenant(params.get('tenant'));
    if (params.get('new') && tenants.length) { setOpen({ tenant_id: params.get('new') }); }
    const id = params.get('open');
    if (id && users.length) { const u = users.find((x) => x.id === id); if (u) setOpen(u); }
  }, [params, users, tenants]);

  const tenantOf = (u) => tenants.find((t) => t.id === (u.tenant?.id)) || null;
  const filtered = useMemo(() => users.filter((u) => {
    if (u.is_superadmin) return false;
    const matchQ = !q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase());
    const matchT = !fTenant || u.tenant?.id === fTenant;
    const matchR = !fRole || u.role === fRole;
    return matchQ && matchT && matchR;
  }), [users, q, fTenant, fRole]);

  const getters = {
    name: (u) => u.name, email: (u) => u.email, tenant: (u) => u.tenant?.name,
    role: (u) => u.role, login: (u) => u.last_login_at || '', state: (u) => (u.active ? 1 : 0),
  };
  const { sorted, sort, toggle } = useSortableTable(filtered, getters, { key: 'name', dir: 'asc' });

  const closePanel = () => { setOpen(null); ['open', 'new'].forEach((k) => params.delete(k)); setParams(params, { replace: true }); };

  const onAction = async (action, u) => {
    if (action === 'edit') return setOpen(u);
    if (action === 'reset') {
      try { const r = await adminApi.resetPassword(u.id); r.email_sent ? toast.success('Lien de réinitialisation envoyé') : toast.error("Jeton créé mais email non envoyé"); }
      catch (e) { toast.error(e.message); } return;
    }
    if (action === 'setpw') return setModal({ type: 'setpw', user: u });
    if (action === 'access') return setModal({ type: 'access', user: u });
    if (action === 'impersonate') return setModal({ type: 'impersonate', user: u });
    if (action === 'delete') {
      const t = toast('Supprimer ' + u.email + ' ?', {
        action: { label: 'Confirmer', onClick: async () => { try { await adminApi.deleteUser(u.id); toast.success('Compte supprimé'); load(); } catch (e) { toast.error(e.message); } } },
        duration: 5000,
      });
      return t;
    }
  };

  return (
    <div data-testid="admin-users" className="relative min-h-[70vh]">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-title text-3xl">Utilisateurs</h1>
        <Button variant="primary" onClick={() => setOpen({ tenant_id: tenants[0]?.id })} data-testid="new-user">Nouvel utilisateur</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input className="vd-input" style={{ width: 'auto', flex: '0 1 240px' }} placeholder="Rechercher (nom ou email)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" />
        <select className="vd-input" style={{ width: 'auto' }} value={fTenant} onChange={(e) => setFTenant(e.target.value)} data-testid="filter-tenant">
          <option value="">Tous les établissements</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="vd-input" style={{ width: 'auto' }} value={fRole} onChange={(e) => setFRole(e.target.value)} data-testid="filter-role">
          <option value="">Tous les rôles</option>
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
        <span className="text-xs text-n600 ml-auto">{sorted.length} résultat·s</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm adm-table">
          <thead><tr className="text-left text-n600 border-b border-hair">
            <SortableTh label="Nom" sortKey="name" sort={sort} toggle={toggle} />
            <SortableTh label="Email" sortKey="email" sort={sort} toggle={toggle} />
            <SortableTh label="Établissement" sortKey="tenant" sort={sort} toggle={toggle} />
            <SortableTh label="Rôle" sortKey="role" sort={sort} toggle={toggle} />
            <SortableTh label="Dernière connexion" sortKey="login" sort={sort} toggle={toggle} />
            <SortableTh label="État" sortKey="state" sort={sort} toggle={toggle} />
            <th></th>
          </tr></thead>
          <tbody>
            {sorted.map((u) => (
              <tr key={u.id} className="border-b border-hair/60 hover:bg-surface/40" data-testid={`user-row-${u.email}`}>
                <td className="py-2"><button className="vd-link font-medium" onClick={() => setOpen(u)}>{u.name}</button><div className="text-xs text-n500">{u.phone || ''}</div></td>
                <td className="adm-mono text-xs">{u.email}</td>
                <td>{u.tenant?.name || '—'}<div className="text-xs text-n500">{TENANT_TYPE_LABELS[u.tenant?.type] || ''}</div></td>
                <td>{ROLE_LABELS[u.role] || u.role}</td>
                <td className="text-n600">{u.last_login_at ? timeAgo(u.last_login_at) : 'jamais'}</td>
                <td><span className={`adm-badge ${u.active ? 'adm-badge-active' : ''}`}>{u.active ? 'actif' : 'bloqué'}</span></td>
                <td className="text-right whitespace-nowrap">
                  <button className="vd-link mr-2" onClick={() => setOpen(u)}>Modifier</button>
                  <button className="vd-link mr-2" onClick={() => (u.active ? adminApi.blockUser(u.id) : adminApi.unblockUser(u.id)).then(() => { toast.success(u.active ? 'Compte bloqué' : 'Compte débloqué'); load(); }).catch((e) => toast.error(e.message))}>{u.active ? 'Bloquer' : 'Débloquer'}</button>
                  <RowMenu user={u} tenant={tenantOf(u)} onAction={(a) => onAction(a, u)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <UserPanel user={open} tenants={tenants} onBack={closePanel} onSaved={load} reload={load} openModal={(t, u) => setModal({ type: t, user: u })} />}
      {modal?.type === 'setpw' && <SetPasswordModal user={modal.user} onClose={() => setModal(null)} />}
      {modal?.type === 'access' && <SendAccessModal user={modal.user} tenant={tenantOf(modal.user)} onClose={() => setModal(null)} />}
      {modal?.type === 'impersonate' && <ImpersonateModal user={modal.user} tenant={tenantOf(modal.user)} onClose={() => setModal(null)} />}
    </div>
  );
}
